import type { Segment } from '$lib/stores/aiFeatures'

export class TextSegmentService {
  /**
   * Split text into segments (paragraphs)
   */
  static segmentText(text: string): Segment[] {
    // Minimum length for a segment to stand alone (characters)
    const MIN_SEGMENT_LENGTH = 200
    
    // Check for YAML front matter and exclude it
    let textToSegment = text
    let yamlOffset = 0
    const yamlMatch = text.match(/^---[\r\n]+[\s\S]*?[\r\n]+---[\r\n]+/)
    
    if (yamlMatch) {
      // Extract text after YAML front matter
      yamlOffset = yamlMatch[0].length
      textToSegment = text.substring(yamlOffset)
    }
    
    // If only YAML or empty after YAML, return empty segments
    if (!textToSegment.trim()) {
      return []
    }
    
    // Split by double newlines
    const rawParagraphs = textToSegment.split(/\n\s*\n/g)
      .map(p => p.trim())
      .filter(p => p.length > 0)
    
    // Merge short paragraphs with next ones, tracking breaks
    const mergedParagraphs: Array<{ text: string, breaks: number[] }> = []
    let currentMerged = ''
    let currentBreaks: number[] = []
    
    for (let i = 0; i < rawParagraphs.length; i++) {
      const para = rawParagraphs[i]
      
      // Check if this is a heading or list item
      const isHeading = /^#{1,6}\s/.test(para)
      const isListItem = /^[\s]*[-*•]\s|^[\s]*\d+[.)]\s/.test(para)
      const nextPara = rawParagraphs[i + 1]
      const isNextHeading = nextPara && /^#{1,6}\s/.test(nextPara)
      
      // Check if current accumulated segment already has a heading
      const hasHeadingInSegment = currentMerged && /^#{1,6}\s/m.test(currentMerged)
      
      if (currentMerged) {
        // Rule 1: If this is a heading and segment already has a heading, start new segment
        if (isHeading && hasHeadingInSegment) {
          mergedParagraphs.push({ text: currentMerged, breaks: currentBreaks })
          currentMerged = para
          currentBreaks = []
        }
        // Rule 2: If next is a heading and current segment has enough content, finish segment
        else if (isNextHeading && currentMerged.length >= MIN_SEGMENT_LENGTH) {
          currentBreaks.push(currentMerged.length)
          currentMerged += '\n\n' + para
          mergedParagraphs.push({ text: currentMerged, breaks: currentBreaks })
          currentMerged = ''
          currentBreaks = []
        }
        // Rule 3: Keep merging if segment is still short
        else if (currentMerged.length < MIN_SEGMENT_LENGTH) {
          currentBreaks.push(currentMerged.length)
          currentMerged += '\n\n' + para
        }
        // Rule 4: Merge short paragraphs (< 80 chars) unless they are headings
        else if (para.length < 80 && !isHeading) {
          currentBreaks.push(currentMerged.length)
          currentMerged += '\n\n' + para
        }
        // Rule 5: If combined would be too long (>700), start new segment
        else if (currentMerged.length + para.length > 700) {
          mergedParagraphs.push({ text: currentMerged, breaks: currentBreaks })
          currentMerged = para
          currentBreaks = []
        }
        // Rule 6: Default - continue merging
        else {
          currentBreaks.push(currentMerged.length)
          currentMerged += '\n\n' + para
        }
      } else {
        // Start new segment
        currentMerged = para
        currentBreaks = []
      }
      
      // Check if this is the last paragraph
      if (i === rawParagraphs.length - 1 && currentMerged) {
        // If last paragraph is too short and there's a previous one, merge
        // Use a more aggressive threshold for the last paragraph (200 chars)
        if (currentMerged.length < 200 && mergedParagraphs.length > 0 && !isListItem && !isHeading) {
          const lastPara = mergedParagraphs.pop()!
          // Only merge if combined length is reasonable (not too long)
          if (lastPara.text.length + currentMerged.length < 600) {
            lastPara.breaks.push(lastPara.text.length)
            lastPara.text = lastPara.text + '\n\n' + currentMerged
            mergedParagraphs.push(lastPara)
          } else {
            // Don't merge if it would create too long segment
            mergedParagraphs.push(lastPara)
            mergedParagraphs.push({ text: currentMerged, breaks: currentBreaks })
          }
        } else {
          mergedParagraphs.push({ text: currentMerged, breaks: currentBreaks })
        }
      }
    }
    
    // Create segments from merged paragraphs
    let currentPosition = yamlOffset // Start after YAML if present
    const segments: Segment[] = []
    
    mergedParagraphs.forEach((item, index) => {
      const startPos = text.indexOf(item.text, currentPosition)
      const endPos = startPos + item.text.length
      
      segments.push({
        id: `segment-${index}`,
        original: item.text,
        suggested: '', // Will be filled by AI
        hasChanges: false,
        selected: false,
        status: 'pending',
        position: {
          start: startPos,
          end: endPos
        },
        paragraphBreaks: item.breaks.length > 0 ? item.breaks : undefined,
        history: [], // Initialize empty history
        historyIndex: -1 // No history yet
      })
      
      currentPosition = endPos
    })
    
    return segments
  }
  
  /**
   * Determine if text should be segmented
   */
  static shouldSegment(text: string): boolean {
    // Remove YAML front matter before checking
    let textToCheck = text
    const yamlMatch = text.match(/^---[\r\n]+[\s\S]*?[\r\n]+---[\r\n]+/)
    if (yamlMatch) {
      textToCheck = text.substring(yamlMatch[0].length)
    }
    
    // Segment if:
    // - Text is long enough AND has multiple paragraphs
    // - Text has many paragraphs regardless of length
    // - Text is very long
    const hasMultipleParagraphs = textToCheck.includes('\n\n')
    const paragraphCount = (textToCheck.match(/\n\n/g) || []).length + 1
    
    // Don't segment short texts
    if (textToCheck.length < 500) {
      return false
    }
    
    // Segment if it has many paragraphs (5+)
    if (paragraphCount >= 5) {
      return true
    }
    
    // Segment if text is long and has clear structure
    if (hasMultipleParagraphs && textToCheck.length > 600) {
      return true
    }
    
    // Segment very long texts
    return textToCheck.length > 1000
  }
  
  /**
   * Apply selected segments back to original text
   */
  static applySegments(originalText: string, segments: Segment[]): string {
    // Sort segments by position to maintain order
    const sortedSegments = [...segments]
      .filter(s => s.selected && s.suggested)
      .sort((a, b) => a.position.start - b.position.start)
    
    // Apply replacements from end to start to maintain positions
    let result = originalText
    for (let i = sortedSegments.length - 1; i >= 0; i--) {
      const segment = sortedSegments[i]
      result = result.substring(0, segment.position.start) + 
               segment.suggested + 
               result.substring(segment.position.end)
    }
    
    return result
  }
  
  /**
   * Check if segments have changes
   */
  static detectChanges(segments: Segment[]): Segment[] {
    return segments.map(segment => ({
      ...segment,
      hasChanges: segment.original !== segment.suggested && segment.suggested !== ''
    }))
  }
  
  /**
   * Get selection context for better AI processing
   */
  static getSelectionContext(text: string, start: number, end: number) {
    const selectedText = text.substring(start, end)
    
    // Get some context before and after
    const contextBefore = text.substring(Math.max(0, start - 100), start)
    const contextAfter = text.substring(end, Math.min(text.length, end + 100))
    
    return {
      selected: selectedText,
      before: contextBefore,
      after: contextAfter,
      fullText: text
    }
  }
  
  /**
   * Preserve paragraph structure in AI-suggested text
   */
  static preserveParagraphStructure(suggested: string, paragraphBreaks: number[] | undefined): string {
    if (!paragraphBreaks || paragraphBreaks.length === 0) {
      return suggested
    }
    
    // If AI already preserved paragraph breaks, return as-is
    const suggestedBreakCount = (suggested.match(/\n\n/g) || []).length
    if (suggestedBreakCount >= paragraphBreaks.length) {
      return suggested
    }
    
    // Split suggested text into sentences or phrases
    const sentences = suggested.match(/[^.!?]+[.!?]+/g) || [suggested]
    
    // Calculate how many sentences should go in each paragraph
    const totalSentences = sentences.length
    const numParagraphs = paragraphBreaks.length + 1
    const sentencesPerPara = Math.ceil(totalSentences / numParagraphs)
    
    // Reconstruct with proper paragraph breaks
    const result: string[] = []
    let sentenceIndex = 0
    
    for (let i = 0; i < numParagraphs; i++) {
      const paraStart = sentenceIndex
      const paraEnd = Math.min(sentenceIndex + sentencesPerPara, totalSentences)
      
      if (paraStart < totalSentences) {
        const paraSentences = sentences.slice(paraStart, paraEnd)
        result.push(paraSentences.join(' ').trim())
        sentenceIndex = paraEnd
      }
    }
    
    return result.join('\n\n')
  }
}